import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const register = async (userData) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            name: userData.name,
            phone: userData.phone
          }
        }
      });

      if (error) throw error;

      if (data.user && !data.user.email_confirmed_at) {
        toast.success('Registration successful! Please check your email to verify your account.');
        return { success: true, needsVerification: true };
      }

      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Registration failed');
      return { success: false, error: error.message };
    }
  };

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Check if email is verified
      if (data.user && !data.user.email_confirmed_at) {
        toast.error('Please verify your email before logging in. Check your inbox for the verification link.');
        return { success: false, error: 'Email not verified', invalidCredentials: false };
      }

      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.message || '';
      const invalidCredentials =
        msg.includes('Invalid login credentials') ||
        error.code === 'invalid_credentials' ||
        /invalid.*(email|password|credentials)/i.test(msg);

      if (invalidCredentials) {
        toast.error('Invalid email or password');
      } else if (msg.includes('Email not confirmed')) {
        toast.error('Please verify your email before logging in');
      } else {
        toast.error(msg || 'Login failed');
      }

      return { success: false, error: msg, invalidCredentials };
    }
  };

  // A logout must never be blockable by the server. supabase.auth.signOut()
  // defaults to global scope and calls the API, which returns 403
  // session_not_found when the refresh token has already been invalidated —
  // and the old code threw on that, showed "Logout failed", and left the stored
  // session in place. The user stayed "logged in" with no way out but clearing
  // site data, and every retry failed identically. Reported live 2026-08-10.
  //
  // The user's intent is to end the session on THIS device, which never
  // requires the server's cooperation: scope 'local' drops the stored session
  // without a network call. Local state is cleared in `finally` so it happens
  // whichever path we took.
  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.warn('Global sign-out failed; clearing local session instead', error);
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (localError) {
        // Nothing left to try server-side; the redirect below still ends the
        // session from the user's point of view.
        console.warn('Local sign-out also failed', localError);
      }
    } finally {
      setUser(null);
      setSessionError(null);
      toast.success('Logged out successfully');
      window.location.href = '/';
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      toast.success('Password reset email sent!');
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(error.message || 'Password reset failed');
      return { success: false, error: error.message };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success('Password updated successfully!');
      return { success: true };
    } catch (error) {
      console.error('Password update error:', error);
      toast.error(error.message || 'Password update failed');
      return { success: false, error: error.message };
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email
      });

      if (error) throw error;

      toast.success('Verification email sent!');
      return { success: true };
    } catch (error) {
      console.error('Resend verification error:', error);
      toast.error(error.message || 'Failed to resend verification email');
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    sessionError,
    register,
    login,
    logout,
    resetPassword,
    updatePassword,
    resendVerificationEmail,
    isAuthenticated: !!user,
    isEmailVerified: user?.email_confirmed_at ? true : false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 